// Pure data layer of the guided first run of Turnos Beauty: initial data, validations and requests.
// Required steps create the salon (multipart, with opening hours and booking policy), then its
// services (JSON, one by one) and its professionals (JSON, referencing the created services).
// Optional steps update the salon through /salon/update sending only their own fields.
import { COUNTRIES } from "../../../pages/reservas/components/ReservaShared";
import {
	faltaListaEspera,
	faltaResena,
	faltaWhatsapp,
	recordatoriosDeVenue,
	recordatoriosJson,
} from "../../recordatorios/recordatorios";
import {
	DIAS_SEMANA,
	MAX_GALERIA,
	coordValida,
	faltaContacto,
	faltaUbicacion,
	num,
	telefonoCompleto,
} from "../../reservas/primerUso/barPayload";

export {
	DIAS_SEMANA,
	MAX_GALERIA,
	coordValida,
	faltaContacto,
	faltaUbicacion,
	num,
	telefonoCompleto,
};

// Tab of the salon module where each optional step is completed later.
export const TAB_DEL_PASO = {
	contacto: "info",
	fotos: "info",
};

let contador = 0;
const nuevaClave = (prefijo) => {
	contador += 1;
	return `${prefijo}${contador}`;
};

export const nuevoServicio = () => ({
	key: nuevaClave("srv"),
	nombre: "",
	categoria: "",
	duracion: "60",
	precio: "",
	sena: "",
});

export const nuevoProfesional = () => ({
	key: nuevaClave("pro"),
	nombre: "",
	especialidad: "",
	serviceKeys: [],
	propio: false,
	horario: null,
});

// Front-only examples (the back has no catalog): the owner edits or removes them before publishing.
const serviciosDeEjemplo = () =>
	[
		["Corte", "Peluquería", "45", "15000"],
		["Color", "Peluquería", "90", "35000"],
		["Brushing", "Peluquería", "30", "9000"],
		["Manicuría", "Uñas", "60", "12000"],
	].map(([nombre, categoria, duracion, precio]) => ({
		...nuevoServicio(),
		nombre,
		categoria,
		duracion,
		precio,
	}));

/** Starting values; the name comes from the portal negocio and stays editable. */
export const datosInicialesSalon = (negocio = "") => ({
	nombre: negocio,
	nombreDelPortal: negocio,
	onlineBookingEnabled: false,
	stateid: "",
	cityid: "",
	address: "",
	lat: "",
	lng: "",
	dias: [1, 2, 3, 4, 5, 6],
	desde: "09:00",
	hasta: "20:00",
	servicios: serviciosDeEjemplo(),
	equipo: [],
	// "¿Cómo se reserva?" (same names as the shared policies step); the deposit mode is derived, see hayCobroAnticipado
	ventana: 30,
	antelacion: 120,
	tolerancia: 15,
	cancela: true,
	plazo: 24,
	notas: "",
	// optional steps
	descripcion: "",
	prefijo: COUNTRIES[0].prefix,
	telefono: "",
	email: "",
	web: "",
	instagram: "",
	portada: null,
	galeria: [],
});

/* ---- validations (return the first missing thing, "" when fine) ---- */

export const faltaSalon = (d) =>
	!d.nombre.trim() ? "Falta el nombre del salón" : faltaUbicacion(d);

export const faltaHorarios = (d) =>
	!d.dias.length
		? "Elegí al menos un día"
		: !d.desde || !d.hasta || d.desde === d.hasta
			? "Revisá el horario de apertura y cierre"
			: "";

const enteroMultiploDe5 = (v) =>
	Number.isInteger(Number(v)) && Number(v) >= 5 && Number(v) % 5 === 0;

export const faltaServicios = (d) => {
	if (!d.servicios.length) return "Agregá al menos un servicio";
	for (const s of d.servicios) {
		const nombre = s.nombre.trim() || "un servicio";
		if (!s.nombre.trim()) return "Hay un servicio sin nombre";
		if (!enteroMultiploDe5(s.duracion))
			return `La duración de ${nombre} debe ser múltiplo de 5 minutos`;
		if (s.precio === "" || s.precio == null || !(Number(s.precio) >= 0))
			return `Falta el precio de ${nombre}`;
		if (s.sena !== "" && s.sena != null) {
			if (!(Number(s.sena) >= 0))
				return `La seña de ${nombre} no puede ser negativa`;
			if (Number(s.sena) > Number(s.precio))
				return `La seña de ${nombre} supera su precio`;
		}
	}
	return "";
};

/** Services of a professional that still exist in the services step. */
export const clavesVigentes = (p, d) =>
	p.serviceKeys.filter((k) => d.servicios.some((s) => s.key === k));

/** Weekly schedule a professional really works: the salon's unless they have their own (never on days the salon is closed). */
export const horarioDelPro = (p, d) => {
	if (!(p.propio && p.horario)) return horariosDe(d);
	const fuera = (h) => h.enabled && !d.dias.includes(h.weekday);
	return p.horario.some(fuera)
		? p.horario.map((h) => (fuera(h) ? { ...h, enabled: false } : h))
		: p.horario;
};

/** Same days and hours (hours only matter on open days). */
export const mismoHorario = (a, b) =>
	a.length === b.length &&
	a.every(
		(h, i) =>
			h.weekday === b[i].weekday &&
			h.enabled === b[i].enabled &&
			(!h.enabled || (h.open === b[i].open && h.close === b[i].close)),
	);

/** `serviceKeys`-style patch for a new schedule: back to "same as the salon" when it matches it. */
export const parcheHorario = (horario, d) =>
	mismoHorario(horario, horariosDe(d))
		? { propio: false, horario: null }
		: { propio: true, horario };

export const faltaEquipo = (d) => {
	if (!d.equipo.length) return "Agregá al menos un profesional";
	for (const p of d.equipo) {
		if (!p.nombre.trim()) return "Hay un profesional sin nombre";
		if (!clavesVigentes(p, d).length)
			return `Elegí los servicios de ${p.nombre.trim()}`;
		if (p.propio) {
			const activos = horarioDelPro(p, d).filter((h) => h.enabled);
			if (!activos.length) return `Elegí al menos un día de ${p.nombre.trim()}`;
			if (activos.some((h) => !h.open || !h.close || h.open === h.close)) {
				return `Revisá el horario de ${p.nombre.trim()}`;
			}
		}
	}
	return "";
};

export const faltaReserva = () => "";

export const faltaContactoSalon = (d) => faltaContacto(d);

/* ---- requests ---- */

export const horariosDe = (d) =>
	DIAS_SEMANA.map(({ weekday }) => ({
		weekday,
		enabled: d.dias.includes(weekday),
		open: d.desde,
		close: d.hasta,
	}));

/** Some service carries a deposit: the salon then charges deposits (when the account can charge). */
export const hayCobroAnticipado = (d) =>
	d.servicios.some((s) => Number(s.sena) > 0);

/** `soloGratis` (cobros not validated) forces free mode: the backend answers 403 otherwise. */
export const formDataCrearSalon = (d, { soloGratis = false } = {}) => {
	const fd = new FormData();
	fd.append("name", d.nombre.trim());
	fd.append("address", d.address.trim());
	fd.append("lat", String(d.lat));
	fd.append("lng", String(d.lng));
	fd.append("cityid", d.cityid);
	fd.append("stateid", d.stateid);
	fd.append("openingHours", JSON.stringify(horariosDe(d)));
	fd.append("onlineBookingEnabled", String(d.onlineBookingEnabled === true));
	fd.append(
		"reservationMode",
		!soloGratis && hayCobroAnticipado(d) ? "deposit" : "free",
	);
	fd.append("reservationWindowDays", String(d.ventana));
	fd.append("minAdvanceMinutes", String(d.antelacion));
	fd.append("toleranceMinutes", String(d.tolerancia));
	fd.append("cancellationAllowed", String(Boolean(d.cancela)));
	fd.append("cancellationPolicyHours", String(d.plazo));
	if (d.notas.trim()) fd.append("additionalNotes", d.notas.trim());
	return fd;
};

// `s.extra` (copied services only) carries description, buffer, variants and add-ons of the source service.
export const payloadServicio = (s) => {
	const body = {
		...(s.extra || {}),
		name: s.nombre.trim(),
		price: num(s.precio),
		durationMinutes: Number(s.duracion),
		deposit: num(s.sena),
		active: true,
	};
	if (s.categoria.trim()) body.category = s.categoria.trim();
	return body;
};

/** `idDe` maps a local service key to the id created for it. */
export const payloadProfesional = (p, d, idDe) => ({
	name: p.nombre.trim(),
	specialty: (p.especialidad || "").trim(),
	active: true,
	serviceIds: clavesVigentes(p, d).map(idDe),
	weeklySchedule: horarioDelPro(p, d),
});

const base = (id) => {
	const fd = new FormData();
	fd.append("id", id);
	return fd;
};

export const formDataContacto = (id, d) => {
	const fd = base(id);
	fd.append("description", d.descripcion.trim());
	fd.append("contactPhone", telefonoCompleto(d));
	fd.append("contactEmail", d.email.trim());
	fd.append("website", d.web.trim());
	fd.append("instagram", d.instagram.trim());
	return fd;
};

export const hayFotos = (d) => Boolean(d.portada) || d.galeria.length > 0;

// No existingGallery on purpose: the backend then keeps what is there and appends (max 5).
export const formDataFotos = (id, d) => {
	const fd = base(id);
	if (d.portada) fd.append("image", d.portada);
	d.galeria.slice(0, MAX_GALERIA).forEach((f) => fd.append("galleryimage", f));
	return fd;
};

/**
 * `salon/update` body of a copied salon (`d.importado`): contact data, reminders and rescheduling, which
 * the create request does not carry. Client fields go apart (`formDataCamposImportados`): owner-only.
 */
export const formDataAjustesImportados = (id, d) => {
	const fd = formDataContacto(id, d);
	const imp = d.importado || {};
	if (imp.reminders) fd.append("reminders", recordatoriosJson(recordatoriosDeVenue(imp.reminders)));
	if (imp.rescheduling) fd.append("rescheduling", JSON.stringify(imp.rescheduling));
	return fd;
};

/** Client field definitions of the source, without their ids so the new salon creates its own. */
export const formDataCamposImportados = (id, d) => {
	const fd = base(id);
	const campos = (d.importado?.clientFields || []).map(({ id: _id, ...resto }) => resto);
	fd.append("clientFields", JSON.stringify(payloadCamposCliente(camposDeCliente(campos))));
	return fd;
};

/* ---- edit form (SALÓN tab): salon -> form data and only-what-changed update ---- */

const refId = (v) => v?._id || v || "";

const horariosEditables = (openingHours) => {
	const porDia = new Map(
		(Array.isArray(openingHours) ? openingHours : []).map((h) => [
			Number(h.weekday),
			h,
		]),
	);
	return DIAS_SEMANA.map(({ weekday }) => {
		const h = porDia.get(weekday);
		return {
			weekday,
			enabled: Boolean(h?.enabled),
			open: h?.open || "09:00",
			close: h?.close || "20:00",
		};
	});
};

export const separarTelefono = (telefono = "") => {
	const pais =
		COUNTRIES.find((c) => telefono.startsWith(c.prefix)) || COUNTRIES[0];
	return {
		prefijo: pais.prefix,
		telefono: telefono.startsWith(pais.prefix)
			? telefono.slice(pais.prefix.length).trim()
			: telefono.trim(),
	};
};

/** Form values (same names as the guided flow) from a salon returned by GET /salon. */
export const datosDeSalon = (s = {}) => ({
	nombre: s.name || "",
	// Legacy salons have no value and count as enabled; only an explicit false blocks public booking.
	onlineBookingEnabled: s.onlineBookingEnabled !== false,
	stateid: refId(s.stateid),
	cityid: refId(s.cityid),
	address: s.address || "",
	lat: s.lat ?? "",
	lng: s.lng ?? "",
	horarios: horariosEditables(s.openingHours),
	ventana: s.reservationWindowDays ?? 6,
	antelacion: s.minAdvanceMinutes ?? 120,
	tolerancia: s.toleranceMinutes ?? 15,
	...recordatoriosDeVenue(s.reminders),
	cancela: s.cancellationAllowed !== false,
	plazo: s.cancellationPolicyHours ?? 24,
	// Rescheduling reuses the cancellation deadline; enabled by default, at most 2 moves per turno.
	reagendar: s.rescheduling?.enabled !== false,
	reagendarMax: Number(s.rescheduling?.maxPerAppointment) || 2,
	notas: s.additionalNotes || "",
	clientFields: camposDeCliente(s.clientFields),
	descripcion: s.description || "",
	...separarTelefono(s.contactPhone || ""),
	email: s.contactEmail || "",
	web: s.website || "",
	instagram: s.instagram || "",
	portada: null,
	galeria: [],
});

export const MAX_CLIENT_FIELDS = 20;

export const TIPOS_CAMPO = [
	{ value: "text", label: "Texto corto" },
	{ value: "longtext", label: "Texto largo" },
	{ value: "number", label: "Número" },
	{ value: "date", label: "Fecha" },
	{ value: "select", label: "Lista de opciones" },
	{ value: "checkbox", label: "Casilla (sí / no)" },
];

let campoNuevo = 0;
/** Blank definition; it has no `id` until the server creates it (that is also what keeps its type editable). */
export const nuevoCampoCliente = () => {
	campoNuevo += 1;
	return {
		_key: `nuevo${campoNuevo}`,
		label: "",
		type: "text",
		options: [],
		required: false,
		askAtBooking: false,
		archived: false,
	};
};

const campoEditable = (f) => ({
	id: f.id,
	label: f.label || "",
	type: f.type || "text",
	options: Array.isArray(f.options) ? f.options.map(String) : [],
	required: Boolean(f.required),
	askAtBooking: Boolean(f.askAtBooking),
	archived: Boolean(f.archived),
});

/** Editable copy of `salon.clientFields` (every definition, archived ones included: the server archives what is omitted). */
export const camposDeCliente = (lista) =>
	Array.isArray(lista) ? lista.map(campoEditable) : [];

/** Body value for `clientFields`: no local keys, blank select options dropped. New definitions go without `id`. */
export const payloadCamposCliente = (lista) =>
	lista.map((f) => ({
		...(f.id ? { id: f.id } : {}),
		label: f.label.trim(),
		type: f.type,
		options:
			f.type === "select" ? f.options.map((o) => o.trim()).filter(Boolean) : [],
		required: Boolean(f.required),
		askAtBooking: Boolean(f.askAtBooking),
		archived: Boolean(f.archived),
	}));

export const faltaCamposCliente = (lista = []) => {
	if (lista.length > MAX_CLIENT_FIELDS)
		return `Podés tener hasta ${MAX_CLIENT_FIELDS} campos (los archivados cuentan)`;
	for (const f of lista) {
		const nombre = f.label.trim();
		if (!nombre) return "Hay un campo de cliente sin nombre";
		if (nombre.length > 60)
			return `El nombre del campo "${nombre.slice(0, 20)}…" supera los 60 caracteres`;
		if (f.type === "select") {
			const opciones = f.options.map((o) => o.trim()).filter(Boolean);
			if (opciones.length < 1 || opciones.length > 20)
				return `"${nombre}" necesita entre 1 y 20 opciones`;
			if (opciones.some((o) => o.length > 60))
				return `Una opción de "${nombre}" supera los 60 caracteres`;
		}
	}
	return "";
};

export const faltaHorariosSalon = (d) => {
	const raro = d.horarios.find(
		(h) => h.enabled && (!h.open || !h.close || h.open === h.close),
	);
	if (raro) {
		const dia =
			DIAS_SEMANA.find((x) => x.weekday === raro.weekday)?.largo || "un día";
		return `Revisá el horario del ${dia.toLowerCase()}`;
	}
	return "";
};

/** Location is only validated when it was touched, so a legacy salon without a pin can still be saved. */
export const faltaSalonEditado = (d, original) => {
	if (!d.nombre.trim()) return "Falta el nombre del salón";
	const cambioUbicacion = ["stateid", "cityid", "address", "lat", "lng"].some(
		(k) => d[k] !== original[k],
	);
	return (
		(cambioUbicacion && faltaUbicacion(d)) ||
		faltaHorariosSalon(d) ||
		faltaWhatsapp(d.whatsapp) ||
		faltaResena(d.resena) ||
		faltaListaEspera(d.listaEspera) ||
		faltaContacto(d) ||
		faltaCamposCliente(d.clientFields)
	);
};

/**
 * POST /salon/update body: `id` plus only the fields that differ from `original`. The reservation mode is
 * never sent from here: it is derived from the deposits (see salonModo.js).
 */
export const formDataActualizarSalon = (id, d, original) => {
	const fd = base(id);
	const cambio = (...claves) =>
		claves.some((k) => JSON.stringify(d[k]) !== JSON.stringify(original[k]));
	if (cambio("nombre")) fd.append("name", d.nombre.trim());
	if (cambio("descripcion")) fd.append("description", d.descripcion.trim());
	if (cambio("stateid", "cityid", "address", "lat", "lng")) {
		fd.append("stateid", d.stateid);
		fd.append("cityid", d.cityid);
		fd.append("address", d.address.trim());
		// Never send "": its presence would disable the back's address geocoding.
		if (coordValida(d.lat) && coordValida(d.lng)) {
			fd.append("lat", String(d.lat));
			fd.append("lng", String(d.lng));
		}
	}
	if (cambio("horarios")) fd.append("openingHours", JSON.stringify(d.horarios));
	if (cambio("onlineBookingEnabled"))
		fd.append("onlineBookingEnabled", String(d.onlineBookingEnabled === true));
	if (cambio("ventana")) fd.append("reservationWindowDays", String(d.ventana));
	if (cambio("antelacion"))
		fd.append("minAdvanceMinutes", String(d.antelacion));
	if (cambio("tolerancia")) fd.append("toleranceMinutes", String(d.tolerancia));
	if (cambio("cancela"))
		fd.append("cancellationAllowed", String(Boolean(d.cancela)));
	if (cambio("plazo")) fd.append("cancellationPolicyHours", String(d.plazo));
	if (cambio("reagendar", "reagendarMax")) {
		fd.append(
			"rescheduling",
			JSON.stringify({
				enabled: Boolean(d.reagendar),
				maxPerAppointment: Number(d.reagendarMax),
			}),
		);
	}
	// Same shape as `rescheduling`: one JSON string. `enabled` covers every reminder channel.
	if (cambio("recordatoriosActivos", "horasAntesRecordatorio", "whatsapp", "push", "resena", "listaEspera"))
		fd.append("reminders", recordatoriosJson(d));
	// Owner-only; one JSON string like `rescheduling`. Omitted definitions are archived by the server, so the whole list travels.
	if (cambio("clientFields"))
		fd.append(
			"clientFields",
			JSON.stringify(payloadCamposCliente(d.clientFields)),
		);
	if (cambio("notas")) fd.append("additionalNotes", d.notas.trim());
	if (cambio("prefijo", "telefono"))
		fd.append("contactPhone", telefonoCompleto(d));
	if (cambio("email")) fd.append("contactEmail", d.email.trim());
	if (cambio("web")) fd.append("website", d.web.trim());
	if (cambio("instagram")) fd.append("instagram", d.instagram.trim());
	if (d.portada) fd.append("image", d.portada);
	d.galeria.slice(0, MAX_GALERIA).forEach((f) => fd.append("galleryimage", f));
	return fd;
};
